import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Save, LayoutGrid, Monitor, AlignLeft, Plus, X, RefreshCw,
  GripVertical, Eye, EyeOff, LayoutDashboard, Search, List,
  ChevronDown, Columns, Type,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getLayoutConfig, updateLayoutConfig } from '../../utils/api';
import { useAdminLang } from '../AdminLangContext';
import type {
  LayoutConfig, WidgetConfig, WidgetType, ColumnCount, ModalMode,
  ResultSectionConfig, ResultSectionType, ResultsColumnConfig, ItemsPerRow,
  FontSizeScale, FontSizeConfig,
} from '../../utils/types';
import {
  WIDGET_META, RESULT_SECTION_META, DEFAULT_LAYOUT_CONFIG,
  DEFAULT_RESULTS_LAYOUT, DEFAULT_RESULTS_HEADER_SECTIONS, DEFAULT_RESULTS_COLUMNS,
} from '../../utils/types';

interface AdminContext { secret: string }

let _idCounter = 0;
function uniqueId(): string { return `w-${Date.now()}-${++_idCounter}`; }

const WIDGET_TYPES = Object.keys(WIDGET_META) as WidgetType[];
const RESULT_SECTION_TYPES = Object.keys(RESULT_SECTION_META) as ResultSectionType[];

/* ------------------------------------------------------------------ */
/*  DnD types                                                          */
/* ------------------------------------------------------------------ */

type DiscoveryDragSource =
  | { from: 'palette'; widgetType: WidgetType }
  | { from: 'column'; columnId: string; index: number }
  | { from: 'header'; index: number };

type ResultsDragSource =
  | { from: 'results-palette'; sectionType: ResultSectionType }
  | { from: 'results-header'; index: number }
  | { from: 'results-column'; columnId: string; index: number };

/* ------------------------------------------------------------------ */
/*  Draggable Widget Card (Discovery)                                   */
/* ------------------------------------------------------------------ */

interface DraggableWidgetCardProps {
  widget: WidgetConfig;
  onDragStart: () => void;
  onToggle: () => void;
  onRemove: () => void;
  onUpdate: (patch: Partial<WidgetConfig>) => void;
}

function DraggableWidgetCard({ widget, onDragStart, onToggle, onRemove, onUpdate }: DraggableWidgetCardProps) {
  const [expanded, setExpanded] = useState(false);
  const meta = WIDGET_META[widget.type];

  return (
    <div
      draggable
      onDragStart={(e) => { e.stopPropagation(); onDragStart(); }}
      className={`group rounded-lg border bg-card p-3 transition-all cursor-grab active:cursor-grabbing select-none ${
        widget.visible ? '' : 'opacity-60'
      }`}
    >
      <div className="flex items-center gap-2">
        <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="text-base flex-shrink-0">{meta.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{widget.title ?? meta.labelEn}</p>
          <p className="text-xs text-muted-foreground truncate">{meta.label}</p>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
            <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
            {widget.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 hover:text-destructive" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Display mode + items per row controls (below title for narrow columns) */}
      <div className="flex items-center gap-2 mt-1.5 ml-10">
        <div className="flex gap-0.5">
          {(['grid', 'list', 'compact'] as const).map((mode) => (
            <Button
              key={mode}
              variant={widget.displayMode === mode ? 'default' : 'ghost'}
              size="icon"
              className="h-6 w-6"
              onClick={(e) => { e.stopPropagation(); onUpdate({ displayMode: mode }); }}
              title={mode.charAt(0).toUpperCase() + mode.slice(1)}
            >
              {mode === 'grid' ? <LayoutGrid className="h-3 w-3" /> :
               mode === 'list' ? <List className="h-3 w-3" /> :
               <AlignLeft className="h-3 w-3" />}
            </Button>
          ))}
        </div>

        {widget.displayMode === 'grid' && (
          <div className="flex gap-0.5">
            {([2, 3, 4, 5, 6] as ItemsPerRow[]).map((n) => (
              <Button
                key={n}
                variant={(widget.itemsPerRow ?? 4) === n ? 'default' : 'outline'}
                size="icon"
                className="h-6 w-6 text-xs"
                onClick={(e) => { e.stopPropagation(); onUpdate({ itemsPerRow: n }); }}
              >
                {n}
              </Button>
            ))}
          </div>
        )}
      </div>

      {expanded && (
        <div className="mt-2 pt-2 border-t grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Custom title</Label>
            <Input className="h-7 text-xs" placeholder={meta.label} value={widget.title ?? ''} onChange={(e) => onUpdate({ title: e.target.value || undefined })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Max items</Label>
            <Input className="h-7 text-xs" type="number" min={1} max={50} placeholder="Default" value={widget.maxItems ?? ''}
              onChange={(e) => { const v = parseInt(e.target.value, 10); onUpdate({ maxItems: isNaN(v) ? undefined : v }); }} />
          </div>
          {widget.type === 'trending-searches' && (
            <div className="space-y-1 col-span-2">
              <Label className="text-xs text-muted-foreground">Display style</Label>
              <Select
                value={widget.trendingVariant ?? 'chips'}
                onValueChange={(v) => onUpdate({ trendingVariant: v as WidgetConfig['trendingVariant'] })}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="chips">Polished chips</SelectItem>
                  <SelectItem value="numbered-list">Numbered list</SelectItem>
                  <SelectItem value="tile-row">Tile row (horizontal scroll)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Draggable Result Section Card                                       */
/* ------------------------------------------------------------------ */

interface DraggableResultCardProps {
  section: ResultSectionConfig;
  onDragStart: () => void;
  onToggle: () => void;
  onRemove?: () => void;
  onUpdate: (patch: Partial<ResultSectionConfig>) => void;
  isDropTarget?: boolean;
  defaultItemsPerRow: ItemsPerRow;
}

function DraggableResultCard({ section, onDragStart, onToggle, onRemove, onUpdate, isDropTarget, defaultItemsPerRow }: DraggableResultCardProps) {
  const meta = RESULT_SECTION_META[section.type];
  const supportsDisplayMode = section.type === 'product-results' || section.type === 'article-results';
  const supportsItemsPerRow = section.type === 'product-results';
  const supportsTileSize = section.type === 'product-results';

  return (
    <div
      draggable
      onDragStart={(e) => { e.stopPropagation(); onDragStart(); }}
      className={`group rounded-lg border bg-card p-3 transition-all cursor-grab active:cursor-grabbing select-none ${
        section.visible ? '' : 'opacity-60'
      } ${isDropTarget ? 'border-primary border-2 scale-[1.01]' : ''}`}
    >
      <div className="flex items-center gap-2">
        <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="text-base">{meta.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{section.title ?? meta.labelEn}</p>
          <p className="text-xs text-muted-foreground">{meta.label}</p>
        </div>

        <div className="flex items-center gap-1">
          <Switch checked={section.visible} onCheckedChange={() => onToggle()} aria-label={`Toggle ${meta.labelEn}`} />
          {onRemove && (
            <Button variant="ghost" size="icon" className="h-6 w-6 hover:text-destructive"
              onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Remove">
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Controls row */}
      {(supportsDisplayMode || supportsItemsPerRow || supportsTileSize) && (
        <div className="flex items-center gap-3 mt-2 ml-10 flex-wrap">
          {supportsDisplayMode && (
            <div className="flex gap-0.5">
              <Button variant={(section.displayMode ?? 'grid') === 'grid' ? 'default' : 'ghost'} size="icon" className="h-6 w-6"
                onClick={(e) => { e.stopPropagation(); onUpdate({ displayMode: 'grid' }); }} title="Grid">
                <LayoutGrid className="h-3 w-3" />
              </Button>
              <Button variant={section.displayMode === 'list' ? 'default' : 'ghost'} size="icon" className="h-6 w-6"
                onClick={(e) => { e.stopPropagation(); onUpdate({ displayMode: 'list' }); }} title="List">
                <List className="h-3 w-3" />
              </Button>
            </div>
          )}

          {supportsItemsPerRow && (
            <div className="flex gap-0.5">
              {([2, 3, 4, 5, 6] as ItemsPerRow[]).map((n) => (
                <Button key={n} variant={(section.itemsPerRow ?? defaultItemsPerRow) === n ? 'default' : 'outline'}
                  size="icon" className="h-6 w-6 text-xs"
                  onClick={(e) => { e.stopPropagation(); onUpdate({ itemsPerRow: n }); }}>
                  {n}
                </Button>
              ))}
            </div>
          )}

          {supportsTileSize && (
            <div className="flex gap-0.5 items-center">
              <span className="text-[10px] text-muted-foreground mr-0.5">Tile:</span>
              {(['small', 'medium', 'large'] as const).map((sz) => (
                <Button key={sz} variant={(section.tileSize ?? 'medium') === sz ? 'default' : 'outline'}
                  size="sm" className="h-6 px-2 text-[10px]"
                  onClick={(e) => { e.stopPropagation(); onUpdate({ tileSize: sz }); }}>
                  {sz.charAt(0).toUpperCase() + sz.slice(1)}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Font size options                                                    */
/* ------------------------------------------------------------------ */

const FONT_SIZE_OPTIONS: { value: FontSizeScale; label: string }[] = [
  { value: 'xs', label: 'XS (12px)' },
  { value: 'sm', label: 'SM (14px)' },
  { value: 'base', label: 'Base (16px)' },
  { value: 'lg', label: 'LG (18px)' },
  { value: 'xl', label: 'XL (20px)' },
];

/* ------------------------------------------------------------------ */
/*  Column Width Slider                                                  */
/* ------------------------------------------------------------------ */

interface ColumnWidthSliderProps {
  columns: Array<{ widthPercent?: number }>;
  columnCount: number;
  onResize: (widths: number[]) => void;
}

function ColumnWidthSlider({ columns, columnCount, onResize }: ColumnWidthSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<number | null>(null);
  const widthsRef = useRef<number[]>([]);

  const visibleCols = columns.slice(0, columnCount);
  const widths = visibleCols.map((c) => c.widthPercent ?? Math.floor(100 / columnCount));

  // Normalize widths so they sum to 100
  const sum = widths.reduce((a, b) => a + b, 0);
  const normalized = widths.map((w) => Math.round((w / sum) * 100));

  const handleMouseDown = useCallback((handleIndex: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = handleIndex;
    widthsRef.current = [...normalized];

    const onMouseMove = (ev: MouseEvent) => {
      if (draggingRef.current === null || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = Math.round(((ev.clientX - rect.left) / rect.width) * 100);
      const idx = draggingRef.current;
      const newWidths = [...widthsRef.current];

      // Compute cumulative width before the handle
      let beforeSum = 0;
      for (let i = 0; i <= idx; i++) beforeSum += newWidths[i];

      // Set left side to pct, right side gets the remainder
      const leftWidth = Math.max(15, Math.min(pct, 100 - 15 * (columnCount - idx - 1)));
      const rightWidth = beforeSum + newWidths[idx + 1] - leftWidth;
      if (rightWidth < 15) return;

      newWidths[idx] = leftWidth - (beforeSum - newWidths[idx]);
      newWidths[idx + 1] = rightWidth;

      // Clamp minimums
      if (newWidths[idx] < 15 || newWidths[idx + 1] < 15) return;

      widthsRef.current = newWidths;
      onResize(newWidths);
    };

    const onMouseUp = () => {
      draggingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [normalized, columnCount, onResize]);

  if (columnCount <= 1) return null;

  return (
    <div ref={containerRef} className="relative flex items-center h-8 mb-2">
      {normalized.map((w, i) => (
        <div key={i} className="flex items-center" style={{ width: `${w}%` }}>
          <span className="flex-1 text-center text-xs font-medium text-muted-foreground">{w}%</span>
          {i < columnCount - 1 && (
            <div
              className="w-4 h-8 flex items-center justify-center cursor-col-resize group flex-shrink-0"
              onMouseDown={handleMouseDown(i)}
            >
              <div className="w-1 h-6 rounded-full bg-muted-foreground/30 group-hover:bg-primary group-active:bg-primary transition-colors" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page component                                                       */
/* ------------------------------------------------------------------ */

export default function AdminLayoutPage() {
  const { secret } = useOutletContext<AdminContext>();
  const { lang } = useAdminLang();

  const [layout, setLayout] = useState<LayoutConfig>({
    ...DEFAULT_LAYOUT_CONFIG,
    resultsLayout: DEFAULT_RESULTS_LAYOUT,
    resultsHeaderSections: DEFAULT_RESULTS_HEADER_SECTIONS,
    resultsColumns: DEFAULT_RESULTS_COLUMNS,
    resultsColumnCount: 1,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);
  const [activeTab, setActiveTab] = useState<'discovery' | 'results'>('discovery');

  // Discovery DnD state
  const discoveryDragRef = useRef<DiscoveryDragSource | null>(null);
  const [discoveryDropTarget, setDiscoveryDropTarget] = useState<string | null>(null);

  // Results DnD state
  const resultsDragRef = useRef<ResultsDragSource | null>(null);
  const [resultsDropTarget, setResultsDropTarget] = useState<string | null>(null);

  // Load config from backend
  useEffect(() => {
    setLoading(true);
    getLayoutConfig(secret, lang)
      .then((cfg) => {
        if (cfg) setLayout({
          ...DEFAULT_LAYOUT_CONFIG,
          ...cfg,
          resultsLayout: cfg.resultsLayout ?? DEFAULT_RESULTS_LAYOUT,
          resultsHeaderSections: cfg.resultsHeaderSections ?? DEFAULT_RESULTS_HEADER_SECTIONS,
          resultsColumns: cfg.resultsColumns ?? DEFAULT_RESULTS_COLUMNS,
          resultsColumnCount: cfg.resultsColumnCount ?? 1,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [secret, lang]);

  /* ================================================================ */
  /*  DISCOVERY LAYOUT — Widget mutators + DnD                          */
  /* ================================================================ */

  const updateHeaderWidget = useCallback((id: string, patch: Partial<WidgetConfig>) => {
    setLayout((prev) => ({ ...prev, headerWidgets: prev.headerWidgets.map((w) => w.id === id ? { ...w, ...patch } : w) }));
  }, []);

  const removeHeaderWidget = useCallback((id: string) => {
    setLayout((prev) => ({ ...prev, headerWidgets: prev.headerWidgets.filter((w) => w.id !== id) }));
  }, []);

  const updateColumnWidget = useCallback((columnId: string, id: string, patch: Partial<WidgetConfig>) => {
    setLayout((prev) => ({
      ...prev,
      columns: prev.columns.map((col) => col.id !== columnId ? col : { ...col, widgets: col.widgets.map((w) => w.id === id ? { ...w, ...patch } : w) }),
    }));
  }, []);

  const removeColumnWidget = useCallback((columnId: string, id: string) => {
    setLayout((prev) => ({ ...prev, columns: prev.columns.map((col) => col.id !== columnId ? col : { ...col, widgets: col.widgets.filter((w) => w.id !== id) }) }));
  }, []);

  const setColumnCount = (n: string) => {
    const count = parseInt(n, 10) as ColumnCount;
    setLayout((prev) => {
      let cols = [...prev.columns];
      while (cols.length < count) cols.push({ id: `col-${Date.now()}-${cols.length}`, widgets: [] });
      if (cols.length > count) {
        // Move orphaned widgets to last kept column
        const kept = cols.slice(0, count);
        const orphaned = cols.slice(count).flatMap((c) => c.widgets);
        if (orphaned.length > 0) kept[kept.length - 1] = { ...kept[kept.length - 1], widgets: [...kept[kept.length - 1].widgets, ...orphaned] };
        cols = kept;
      }
      // Clear stale widthPercent when switching to 1 column
      if (count === 1) {
        cols = cols.map((c) => { const { widthPercent: _, ...rest } = c; return rest; });
      }
      return { ...prev, columnCount: count, columns: cols };
    });
  };

  const updateDiscoveryColumnWidths = useCallback((widths: number[]) => {
    setLayout((prev) => ({
      ...prev,
      columns: prev.columns.map((col, i) => ({
        ...col,
        widthPercent: widths[i],
      })),
    }));
  }, []);

  // Discovery DnD: move widget between zones
  const moveDiscoveryWidget = useCallback((source: DiscoveryDragSource, targetZone: string) => {
    setLayout((prev) => {
      let widget: WidgetConfig | undefined;
      let next = { ...prev };

      // Extract from source
      if (source.from === 'palette') {
        widget = { id: uniqueId(), type: source.widgetType, visible: true };
      } else if (source.from === 'header') {
        widget = prev.headerWidgets[source.index];
        next = { ...next, headerWidgets: next.headerWidgets.filter((_, i) => i !== source.index) };
      } else if (source.from === 'column') {
        const col = prev.columns.find((c) => c.id === source.columnId);
        widget = col?.widgets[source.index];
        next = { ...next, columns: next.columns.map((c) => c.id !== source.columnId ? c : { ...c, widgets: c.widgets.filter((_, i) => i !== source.index) }) };
      }
      if (!widget) return prev;

      // Insert into target
      if (targetZone === 'header') {
        next = { ...next, headerWidgets: [...next.headerWidgets, widget] };
      } else {
        next = { ...next, columns: next.columns.map((c) => c.id !== targetZone ? c : { ...c, widgets: [...c.widgets, widget!] }) };
      }
      return next;
    });
  }, []);

  /* ================================================================ */
  /*  RESULTS LAYOUT — Section mutators + DnD                           */
  /* ================================================================ */

  const resultsHeaderSections = layout.resultsHeaderSections ?? DEFAULT_RESULTS_HEADER_SECTIONS;
  const resultsColumns = layout.resultsColumns ?? DEFAULT_RESULTS_COLUMNS;
  const resultsColumnCount = layout.resultsColumnCount ?? 1;

  const updateResultHeaderSection = useCallback((id: string, patch: Partial<ResultSectionConfig>) => {
    setLayout((prev) => ({
      ...prev,
      resultsHeaderSections: (prev.resultsHeaderSections ?? DEFAULT_RESULTS_HEADER_SECTIONS).map((s) => s.id === id ? { ...s, ...patch } : s),
    }));
  }, []);

  const removeResultHeaderSection = useCallback((id: string) => {
    setLayout((prev) => ({
      ...prev,
      resultsHeaderSections: (prev.resultsHeaderSections ?? DEFAULT_RESULTS_HEADER_SECTIONS).filter((s) => s.id !== id),
    }));
  }, []);

  const updateResultColumnSection = useCallback((columnId: string, id: string, patch: Partial<ResultSectionConfig>) => {
    setLayout((prev) => ({
      ...prev,
      resultsColumns: (prev.resultsColumns ?? DEFAULT_RESULTS_COLUMNS).map((col) =>
        col.id !== columnId ? col : { ...col, sections: col.sections.map((s) => s.id === id ? { ...s, ...patch } : s) }
      ),
    }));
  }, []);

  const removeResultColumnSection = useCallback((columnId: string, id: string) => {
    setLayout((prev) => ({
      ...prev,
      resultsColumns: (prev.resultsColumns ?? DEFAULT_RESULTS_COLUMNS).map((col) =>
        col.id !== columnId ? col : { ...col, sections: col.sections.filter((s) => s.id !== id) }
      ),
    }));
  }, []);

  const setResultsColumnCountFn = (n: string) => {
    const count = parseInt(n, 10) as ColumnCount;
    setLayout((prev) => {
      let cols = [...(prev.resultsColumns ?? DEFAULT_RESULTS_COLUMNS)];
      while (cols.length < count) cols.push({ id: `rcol-${Date.now()}-${cols.length}`, sections: [] });
      if (cols.length > count) {
        const kept = cols.slice(0, count);
        const orphaned = cols.slice(count).flatMap((c) => c.sections);
        if (orphaned.length > 0) kept[kept.length - 1] = { ...kept[kept.length - 1], sections: [...kept[kept.length - 1].sections, ...orphaned] };
        cols = kept;
      }
      // Clear stale widthPercent when switching to 1 column (custom widths are meaningless)
      if (count === 1) {
        cols = cols.map((c) => { const { widthPercent: _, ...rest } = c; return rest; });
      }
      return { ...prev, resultsColumnCount: count, resultsColumns: cols };
    });
  };

  const updateResultsColumnWidths = useCallback((widths: number[]) => {
    setLayout((prev) => ({
      ...prev,
      resultsColumns: (prev.resultsColumns ?? DEFAULT_RESULTS_COLUMNS).map((col, i) => ({
        ...col,
        widthPercent: widths[i],
      })),
    }));
  }, []);

  const updateFontSize = useCallback((key: keyof FontSizeConfig, value: FontSizeScale) => {
    setLayout((prev) => ({
      ...prev,
      fontSizes: { ...prev.fontSizes, [key]: value },
    }));
  }, []);

  // Results DnD: move section between zones
  const moveResultSection = useCallback((source: ResultsDragSource, targetZone: string) => {
    setLayout((prev) => {
      let section: ResultSectionConfig | undefined;
      let next = { ...prev };
      const headerSections = [...(prev.resultsHeaderSections ?? DEFAULT_RESULTS_HEADER_SECTIONS)];
      const cols = (prev.resultsColumns ?? DEFAULT_RESULTS_COLUMNS).map((c) => ({ ...c, sections: [...c.sections] }));

      // Extract from source
      if (source.from === 'results-palette') {
        section = { id: uniqueId(), type: source.sectionType, visible: true };
      } else if (source.from === 'results-header') {
        section = headerSections[source.index];
        headerSections.splice(source.index, 1);
      } else if (source.from === 'results-column') {
        const col = cols.find((c) => c.id === source.columnId);
        if (col) {
          section = col.sections[source.index];
          col.sections.splice(source.index, 1);
        }
      }
      if (!section) return prev;

      // Insert into target
      if (targetZone === 'results-header') {
        headerSections.push(section);
      } else {
        const targetCol = cols.find((c) => c.id === targetZone);
        if (targetCol) targetCol.sections.push(section);
      }

      return { ...next, resultsHeaderSections: headerSections, resultsColumns: cols };
    });
  }, []);

  /* ================================================================ */
  /*  Save / Reset                                                      */
  /* ================================================================ */

  const handleSave = async () => {
    setSaving(true); setSaveError(null); setSaveSuccess(false);
    try {
      await updateLayoutConfig(secret, layout, lang);
      setSaveSuccess(true);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    if (confirm('Reset layout to defaults?')) setLayout({
      ...DEFAULT_LAYOUT_CONFIG,
      resultsLayout: DEFAULT_RESULTS_LAYOUT,
      resultsHeaderSections: DEFAULT_RESULTS_HEADER_SECTIONS,
      resultsColumns: DEFAULT_RESULTS_COLUMNS,
      resultsColumnCount: 1,
    });
  };

  /* ================================================================ */
  /*  Discovery DnD drop zone helpers                                   */
  /* ================================================================ */

  function makeDiscoveryDropProps(zoneId: string) {
    return {
      onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDiscoveryDropTarget(zoneId); },
      onDragLeave: () => setDiscoveryDropTarget(null),
      onDrop: (e: React.DragEvent) => {
        e.preventDefault(); setDiscoveryDropTarget(null);
        if (discoveryDragRef.current) { moveDiscoveryWidget(discoveryDragRef.current, zoneId); discoveryDragRef.current = null; }
      },
    };
  }

  function makeResultsDropProps(zoneId: string) {
    return {
      onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setResultsDropTarget(zoneId); },
      onDragLeave: () => setResultsDropTarget(null),
      onDrop: (e: React.DragEvent) => {
        e.preventDefault(); setResultsDropTarget(null);
        if (resultsDragRef.current) { moveResultSection(resultsDragRef.current, zoneId); resultsDragRef.current = null; }
      },
    };
  }

  /* ================================================================ */
  /*  Render                                                            */
  /* ================================================================ */

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <RefreshCw className="mr-2 h-5 w-5 animate-spin" /> Loading layout...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Layout Configuration</h1>
          <p className="text-muted-foreground">Customize how the search modal appears to users</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset}>Reset defaults</Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save Layout'}
          </Button>
        </div>
      </div>

      {/* Status messages */}
      {saveSuccess && <div className="rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-400">Layout saved successfully.</div>}
      {saveError && <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">{saveError}</div>}

      {/* Display Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Monitor className="h-5 w-5" /> Display Settings</CardTitle>
          <CardDescription>Choose how the search overlay is displayed</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Modal Mode</Label>
              <Select value={layout.modalMode} onValueChange={(v) => setLayout((prev) => ({ ...prev, modalMode: v as ModalMode }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fullscreen"><span className="flex items-center gap-2"><Monitor className="h-4 w-4" /> Fullscreen</span></SelectItem>
                  <SelectItem value="dropdown"><span className="flex items-center gap-2"><AlignLeft className="h-4 w-4" /> Dropdown</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Discovery Columns</Label>
              <Select value={String(layout.columnCount)} onValueChange={setColumnCount}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1"><span className="flex items-center gap-2"><AlignLeft className="h-4 w-4" /> 1 Column</span></SelectItem>
                  <SelectItem value="2"><span className="flex items-center gap-2"><Columns className="h-4 w-4" /> 2 Columns</span></SelectItem>
                  <SelectItem value="3"><span className="flex items-center gap-2"><LayoutGrid className="h-4 w-4" /> 3 Columns</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Typography */}
          <div className="mt-6 pt-6 border-t space-y-3">
            <div className="flex items-center gap-2">
              <Type className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-semibold">Font Sizes</Label>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Headings</Label>
                <Select value={layout.fontSizes?.heading ?? 'sm'} onValueChange={(v) => updateFontSize('heading', v as FontSizeScale)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FONT_SIZE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Body Text</Label>
                <Select value={layout.fontSizes?.body ?? 'sm'} onValueChange={(v) => updateFontSize('body', v as FontSizeScale)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FONT_SIZE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Small / Labels</Label>
                <Select value={layout.fontSizes?.small ?? 'xs'} onValueChange={(v) => updateFontSize('small', v as FontSizeScale)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FONT_SIZE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab toggle */}
      <div className="flex gap-2">
        <Button variant={activeTab === 'discovery' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('discovery')}>
          <LayoutDashboard className="mr-2 h-4 w-4" /> Discovery Layout
        </Button>
        <Button variant={activeTab === 'results' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('results')}>
          <Search className="mr-2 h-4 w-4" /> Search Results Layout
        </Button>
      </div>

      {/* ============================================================ */}
      {/*  DISCOVERY TAB                                                 */}
      {/* ============================================================ */}
      {activeTab === 'discovery' && (
        <div className="flex gap-6 items-start">
          {/* Widget palette */}
          <Card className="w-52 flex-shrink-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Widget Palette</CardTitle>
              <CardDescription className="text-xs">Drag widgets to a zone</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {WIDGET_TYPES.map((type) => {
                const meta = WIDGET_META[type];
                return (
                  <div
                    key={type}
                    draggable
                    onDragStart={() => { discoveryDragRef.current = { from: 'palette', widgetType: type }; }}
                    onDragEnd={() => { discoveryDragRef.current = null; }}
                    className="flex items-center gap-2 px-2.5 py-2 rounded-md border bg-card cursor-grab active:cursor-grabbing hover:border-primary/30 transition-colors select-none"
                  >
                    <span className="text-sm">{meta.icon}</span>
                    <span className="text-xs font-medium truncate">{meta.labelEn}</span>
                    <Plus className="h-3 w-3 text-muted-foreground ml-auto" />
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Canvas: header zone + columns */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Header drop zone */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Header Zone (below search input)</p>
              <div
                className={`min-h-[4rem] rounded-xl border-2 border-dashed p-3 space-y-2 transition-all ${
                  discoveryDropTarget === 'header' ? 'border-primary bg-primary/5' : 'border-muted-foreground/20'
                }`}
                {...makeDiscoveryDropProps('header')}
              >
                {layout.headerWidgets.length === 0 && (
                  <div className="flex items-center justify-center h-10 text-xs text-muted-foreground">Drop widgets here</div>
                )}
                {layout.headerWidgets.map((widget, index) => (
                  <DraggableWidgetCard
                    key={widget.id}
                    widget={widget}
                    onDragStart={() => { discoveryDragRef.current = { from: 'header', index }; }}
                    onToggle={() => updateHeaderWidget(widget.id, { visible: !widget.visible })}
                    onRemove={() => removeHeaderWidget(widget.id)}
                    onUpdate={(patch) => updateHeaderWidget(widget.id, patch)}
                  />
                ))}
              </div>
            </div>

            {/* Column drop zones */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Columns ({layout.columnCount})</p>
              {layout.columnCount > 1 && (
                <ColumnWidthSlider
                  columns={layout.columns}
                  columnCount={layout.columnCount}
                  onResize={updateDiscoveryColumnWidths}
                />
              )}
              {(() => {
                const discoveryCols = layout.columns.slice(0, layout.columnCount);
                const hasDiscoveryWidths = layout.columnCount > 1 && discoveryCols.some((c) => c.widthPercent != null);
                const defaultW = Math.floor(100 / layout.columnCount);
                // Normalize widths so they sum to 100
                const rawWidths = discoveryCols.map((c) => c.widthPercent ?? defaultW);
                const wSum = rawWidths.reduce((a, b) => a + b, 0);
                const normWidths = rawWidths.map((w) => Math.round((w / wSum) * 100));

                return (
                  <div
                    className={hasDiscoveryWidths ? 'flex gap-4' : `grid gap-4 ${layout.columnCount === 1 ? 'grid-cols-1' : layout.columnCount === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3'}`}
                  >
                    {discoveryCols.map((col, colIdx) => (
                      <div key={col.id} style={hasDiscoveryWidths ? { width: `${normWidths[colIdx]}%` } : undefined} className="min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Column {colIdx + 1}</p>
                        <div
                          className={`min-h-[6rem] rounded-xl border-2 border-dashed p-3 space-y-2 transition-all ${
                            discoveryDropTarget === col.id ? 'border-primary bg-primary/5' : 'border-muted-foreground/20'
                          }`}
                          {...makeDiscoveryDropProps(col.id)}
                        >
                          {col.widgets.length === 0 && (
                            <div className="flex items-center justify-center h-16 text-xs text-muted-foreground">Drop widgets here</div>
                          )}
                          {col.widgets.map((widget, index) => (
                            <DraggableWidgetCard
                              key={widget.id}
                              widget={widget}
                              onDragStart={() => { discoveryDragRef.current = { from: 'column', columnId: col.id, index }; }}
                              onToggle={() => updateColumnWidget(col.id, widget.id, { visible: !widget.visible })}
                              onRemove={() => removeColumnWidget(col.id, widget.id)}
                              onUpdate={(patch) => updateColumnWidget(col.id, widget.id, patch)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div className="rounded-md bg-muted px-4 py-3 text-xs text-muted-foreground flex items-start gap-2">
              <GripVertical className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>Drag widgets from the palette to header or columns. Drag between zones to move. Hover to reveal settings.</span>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/*  RESULTS TAB                                                   */}
      {/* ============================================================ */}
      {activeTab === 'results' && (
        <div className="flex gap-6 items-start">
          {/* Section palette */}
          <Card className="w-52 flex-shrink-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Section Palette</CardTitle>
              <CardDescription className="text-xs">Drag sections to a zone</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {RESULT_SECTION_TYPES.map((type) => {
                const meta = RESULT_SECTION_META[type];
                return (
                  <div
                    key={type}
                    draggable
                    onDragStart={() => { resultsDragRef.current = { from: 'results-palette', sectionType: type }; }}
                    onDragEnd={() => { resultsDragRef.current = null; }}
                    className="flex items-center gap-2 px-2.5 py-2 rounded-md border bg-card cursor-grab active:cursor-grabbing hover:border-primary/30 transition-colors select-none"
                  >
                    <span className="text-sm">{meta.icon}</span>
                    <span className="text-xs font-medium truncate">{meta.labelEn}</span>
                    <Plus className="h-3 w-3 text-muted-foreground ml-auto" />
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Results canvas */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Filter sidebar width */}
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium whitespace-nowrap">Filter Sidebar Width</Label>
              <div className="flex items-center gap-2 flex-1 max-w-xs">
                <input
                  type="range"
                  min={10}
                  max={40}
                  step={1}
                  value={layout.filterColumnWidthPercent ?? 20}
                  onChange={(e) => setLayout((prev) => ({ ...prev, filterColumnWidthPercent: Number(e.target.value) }))}
                  className="flex-1 accent-primary"
                />
                <span className="text-sm font-mono w-10 text-right">{layout.filterColumnWidthPercent ?? 20}%</span>
              </div>
            </div>

            {/* Results column count selector */}
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium">Results Columns</Label>
              <Select value={String(resultsColumnCount)} onValueChange={setResultsColumnCountFn}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1"><span className="flex items-center gap-2"><AlignLeft className="h-4 w-4" /> 1 Column</span></SelectItem>
                  <SelectItem value="2"><span className="flex items-center gap-2"><Columns className="h-4 w-4" /> 2 Columns</span></SelectItem>
                  <SelectItem value="3"><span className="flex items-center gap-2"><LayoutGrid className="h-4 w-4" /> 3 Columns</span></SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Auto-collapse empty columns */}
            {resultsColumnCount > 1 && (
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="autoCollapseEmpty"
                  checked={layout.autoCollapseEmptyColumns ?? false}
                  onChange={(e) => setLayout((prev) => ({ ...prev, autoCollapseEmptyColumns: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="autoCollapseEmpty" className="text-sm font-medium cursor-pointer">
                  Auto-expand columns when others are empty
                </label>
                <span className="text-xs text-muted-foreground">(e.g., if column 2 has no articles, column 1 uses full width)</span>
              </div>
            )}

            {/* Full-width header zone */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Full-Width Sections (above columns)</p>
              <div
                className={`min-h-[4rem] rounded-xl border-2 border-dashed p-3 space-y-2 transition-all ${
                  resultsDropTarget === 'results-header' ? 'border-primary bg-primary/5' : 'border-muted-foreground/20'
                }`}
                {...makeResultsDropProps('results-header')}
              >
                {resultsHeaderSections.length === 0 && (
                  <div className="flex items-center justify-center h-10 text-xs text-muted-foreground">Drop sections here (e.g., AI Context)</div>
                )}
                {resultsHeaderSections.map((section, index) => (
                  <DraggableResultCard
                    key={section.id}
                    section={section}
                    onDragStart={() => { resultsDragRef.current = { from: 'results-header', index }; }}
                    onToggle={() => updateResultHeaderSection(section.id, { visible: !section.visible })}
                    onRemove={section.type === 'banner-inline' ? () => removeResultHeaderSection(section.id) : undefined}
                    onUpdate={(patch) => updateResultHeaderSection(section.id, patch)}
                    defaultItemsPerRow={layout.fullscreenItemsPerRow}
                  />
                ))}
              </div>
            </div>

            {/* Column drop zones with width slider */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Column Sections</p>
              {resultsColumnCount > 1 && (
                <ColumnWidthSlider
                  columns={resultsColumns}
                  columnCount={resultsColumnCount}
                  onResize={updateResultsColumnWidths}
                />
              )}
              <div className={`grid gap-4 ${resultsColumnCount === 1 ? 'grid-cols-1' : resultsColumnCount === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3'}`}>
                {resultsColumns.slice(0, resultsColumnCount).map((col, colIdx) => (
                  <div key={col.id}>
                    <p className="text-xs text-muted-foreground mb-1">Column {colIdx + 1}</p>
                    <div
                      className={`min-h-[6rem] rounded-xl border-2 border-dashed p-3 space-y-2 transition-all ${
                        resultsDropTarget === col.id ? 'border-primary bg-primary/5' : 'border-muted-foreground/20'
                      }`}
                      {...makeResultsDropProps(col.id)}
                    >
                      {col.sections.length === 0 && (
                        <div className="flex items-center justify-center h-16 text-xs text-muted-foreground">Drop sections here</div>
                      )}
                      {col.sections.map((section, index) => (
                        <DraggableResultCard
                          key={section.id}
                          section={section}
                          onDragStart={() => { resultsDragRef.current = { from: 'results-column', columnId: col.id, index }; }}
                          onToggle={() => updateResultColumnSection(col.id, section.id, { visible: !section.visible })}
                          onRemove={section.type === 'banner-inline' ? () => removeResultColumnSection(col.id, section.id) : undefined}
                          onUpdate={(patch) => updateResultColumnSection(col.id, section.id, patch)}
                          defaultItemsPerRow={layout.fullscreenItemsPerRow}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-md bg-muted px-4 py-3 text-xs text-muted-foreground flex items-start gap-2">
              <GripVertical className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                Drag sections from the palette into the full-width zone or columns. Drag between zones to rearrange.
                Use grid/list toggles for display mode. Only banner-inline sections can be removed.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Bottom save */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={handleReset}>Reset defaults</Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save Layout'}
        </Button>
      </div>
    </div>
  );
}
